import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Folder,
  Users,
  Building2,
  MessageSquare,
  Shield,
  Settings,
  Link as LinkIcon,
  Lock,
  Mail,
  ChevronDown,
  UserCheck,
  Info,
  Briefcase
} from 'lucide-react';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: 'bucket' | 'subbuckets' | 'influencers' | 'client' | 'testimonial' | 'userinfluencers' | 'userbrands' | 'social' | 'changePassword' | 'smtp' | 'about' | 'services' | 'contact') => void;
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, isOpen }) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const menuItems = [
    { id: 'bucket', label: 'Bucket', icon: Folder },
    { id: 'subbuckets', label: 'Sub-Buckets', icon: Folder },
    { id: 'influencers', label: 'Influencers', icon: Users },
    { id: 'client', label: 'Client Logos', icon: Building2 },
    { id: 'testimonial', label: 'Testimonials', icon: MessageSquare },
    { id: 'userinfluencers', label: 'User Influencers', icon: UserCheck },
    { id: 'userbrands', label: 'User Brands', icon: Building2 },
    { id: 'about', label: 'About Us', icon: Info },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'contact', label: 'Contact Messages', icon: Mail },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      subItems: [
        { id: 'social', label: 'Social', icon: LinkIcon },
        { id: 'changePassword', label: 'Change Password', icon: Lock },
        { id: 'smtp', label: 'SMTP', icon: Mail },
      ]
    },
  ];

  // Effect to open the settings dropdown if an active section is one of its children
  useEffect(() => {
    const settingsItem = menuItems.find(item => item.id === 'settings');
    if (settingsItem?.subItems?.some(sub => sub.id === activeSection)) {
      setIsSettingsOpen(true);
    }
  }, [activeSection]);


  return (
    <>
      <div className={`fixed left-0 top-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:z-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 border-b border-gray-100"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-poppins text-xl font-bold text-gray-900">Admin</h1>
                <p className="font-inter text-sm text-gray-500">Dashboard</p>
              </div>
            </div>
          </motion.div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;

                if (item.subItems) {
                  const isParentActive = item.subItems.some(sub => sub.id === activeSection);
                  return (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <button
                        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                        className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${isParentActive
                          ? 'bg-primary text-white shadow-lg shadow-primary/25'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="w-5 h-5" />
                          <span className="font-inter font-medium">{item.label}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isSettingsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {isSettingsOpen && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="pl-4 pt-2 space-y-1 overflow-hidden"
                          >
                            {item.subItems.map(subItem => {
                              const SubIcon = subItem.icon;
                              const isSubActive = activeSection === subItem.id;
                              return (
                                <li key={subItem.id}>
                                  <button
                                    onClick={() => onSectionChange(subItem.id as any)}
                                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-md transition-all duration-300 text-sm ${isSubActive
                                      ? 'bg-primary/10 text-primary'
                                      : 'text-gray-600 hover:bg-gray-100'
                                      }`}
                                  >
                                    <SubIcon className="w-4 h-4" />
                                    <span className="font-inter">{subItem.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </motion.li>
                  );
                }

                // Regular menu item
                const isActive = activeSection === item.id;
                return (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <button
                      onClick={() => onSectionChange(item.id as any)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${isActive
                        ? 'bg-primary text-white shadow-lg shadow-primary/25'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                        }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-inter font-medium">{item.label}</span>
                    </button>
                  </motion.li>
                );
              })}
            </ul>
          </nav>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="p-4 border-t border-gray-100"
          >
            {/* Can add footer content here if needed */}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;